import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { questions } from "./Questions";
import IconButton from "@mui/material/IconButton";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ShareIcon from "@mui/icons-material/Share";

const Sparkle = ({ size, color, style }) => {
  const pathLength = Math.random() * 0.5 + 0.5;
  const delay = Math.random() * 2;

  return (
    <motion.svg
      width={size}
      height={size}
      viewBox="0 0 160 160"
      fill="none"
      style={{
        position: "absolute",
        ...style,
      }}
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: [0, 1, 0], scale: [0, 1, 0] }}
      transition={{
        duration: 1.5,
        repeat: Infinity,
        delay,
        ease: "easeInOut",
      }}
    >
      <motion.path
        d="M80 0C80 0 84.2846 41.2925 101.496 58.504C118.707 75.7154 160 80 160 80C160 80 118.707 84.2846 101.496 101.496C84.2846 118.707 80 160 80 160C80 160 75.7154 118.707 58.504 101.496C41.2925 84.2846 0 80 0 80C0 80 41.2925 75.7154 58.504 58.504C75.7154 41.2925 80 0 80 0Z"
        fill={color}
        initial={{ pathLength: 0 }}
        animate={{ pathLength }}
        transition={{
          duration: 1.5,
          repeat: Infinity,
          delay,
          ease: "easeInOut",
        }}
      />
    </motion.svg>
  );
};

const BaseCard = ({
  cardData,
  name,
  showShareButtons = false,
  onShare,
  onCopyLink,
  showSparkles = false,
  showMakeYourOwnButton = false,
  onMakeYourOwn,
}) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [revealStep, setRevealStep] = useState(0);
  const [isAnswerRevealed, setIsAnswerRevealed] = useState(false);
  const [showShareConfirmation, setShowShareConfirmation] = useState(false);
  const [shareMessage, setShareMessage] = useState("");

  // Generate random positions for sparkles
  const sparkles = Array.from({ length: 8 }, (_, i) => ({
    id: i,
    size: Math.random() * 20 + 10,
    color: `hsl(${Math.random() * 60 + 40}, 100%, 70%)`,
    style: {
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
    },
  }));

  const getQuestionEmoji = (index) => {
    const emojis = {
      0: "💭", // memory
      1: "💬", // phrase
      2: "✨", // activity
      3: "🏆", // proud
      4: "😂", // funny
      5: "💪", // strength
      6: "✨", // admire
      7: "🎭", // inherit
      8: "😊", // happy
      9: "📚", // lesson
      10: "🔍", // wish
      11: "🎯", // future
    };
    return emojis[index] || "💫";
  };

  const transformToFirstPerson = (question) => {
    const transformations = {
      "What's your favorite memory together?": "My favorite memory with you",
      "What's a phrase they always say?": "A phrase you always say",
      "What's your favorite activity to do together?":
        "My favorite activity to do with you",
      "What's something you're proud of them for?":
        "Something I'm proud of you for",
      "What's your funniest memory together?": "My funniest memory with you",
      "What's their biggest strength?": "Your biggest strength",
      "What personality trait do you admire most?":
        "A personality trait I admire most about you",
      "What trait did you inherit that you appreciate?":
        "A trait I inherited from you that I appreciate",
      "When did you see them the happiest?": "When I saw you the happiest",
      "What's one lesson they taught you?": "One lesson you taught me",
      "What do you wish you knew about them earlier?":
        "Something I wish I knew about you earlier",
      "What do you hope to do together in the future?":
        "Something I hope to do with you in the future",
    };
    return transformations[question] || question;
  };

  const handleCardTap = () => {
    if (!isFlipped) {
      setIsFlipped(true);
      setTimeout(() => {
        setRevealStep(1);
      }, 1000);
    }
  };

  const handleAnswerReveal = () => {
    if (!isAnswerRevealed) {
      setIsAnswerRevealed(true);
    } else if (revealStep < answeredQuestions.length) {
      setRevealStep((prev) => prev + 1);
      setIsAnswerRevealed(false);
    } else if (revealStep === answeredQuestions.length) {
      setRevealStep((prev) => prev + 1);
      setIsAnswerRevealed(false);
    }
  };

  const handleNext = () => {
    if (isAnswerRevealed) {
      if (revealStep < answeredQuestions.length) {
        setRevealStep((prev) => prev + 1);
        setIsAnswerRevealed(false);
      } else if (revealStep === answeredQuestions.length) {
        setRevealStep((prev) => prev + 1);
        setIsAnswerRevealed(false);
      }
    } else {
      setIsAnswerRevealed(true);
    }
  };

  const answeredQuestions = Object.entries(cardData.answers)
    .filter(([_, value]) => value && value.trim() !== "")
    .map(([index, value]) => ({
      question: questions[parseInt(index)],
      answer: value,
    }));

  return (
    <Box
      sx={{
        width: "100vw",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: `
          radial-gradient(ellipse 80% 60% at 70% 0%, #f3e6fa 60%, transparent 100%),
          linear-gradient(120deg, #f8f6ed 60%, #f3e6fa 100%)
        `,
        backgroundColor: "#f8f6ed",
        p: { xs: 2, sm: 4 },
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Card Container */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 600,
          minWidth: 280,
          mx: "auto",
          position: "relative",
          minHeight: { xs: 540, sm: 600 },
          height: { xs: 540, sm: "70vh" },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          style={{
            width: "100%",
            height: "100%",
            position: "relative",
            perspective: "1000px",
          }}
        >
          <motion.div
            style={{
              width: "100%",
              height: "100%",
              position: "relative",
              transformStyle: "preserve-3d",
              transition: "transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
              transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
            }}
          >
            {/* Front of card */}
            <Box
              onClick={handleCardTap}
              sx={{
                width: "100%",
                height: "100%",
                bgcolor: "white",
                borderRadius: "24px",
                boxShadow:
                  "0 16px 48px 0 rgba(124,92,255,0.13), 0 2px 8px 0 rgba(0,0,0,0.04)",
                mx: "auto",
                p: { xs: 4, sm: 6 },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                backgroundImage:
                  "linear-gradient(to bottom, rgba(255,255,255,0.95), rgba(255,255,255,0.98)), url('/assets/paper-texture.png')",
                backgroundSize: "cover",
                backgroundBlendMode: "overlay",
                overflow: "hidden",
                backfaceVisibility: "hidden",
                cursor: isFlipped ? "default" : "pointer",
                pointerEvents: isFlipped ? "none" : "auto",
                zIndex: 2,
                position: "relative",
              }}
            >
              {/* Sparkles */}
              {showSparkles &&
                sparkles.map((sparkle) => (
                  <Sparkle
                    key={sparkle.id}
                    size={sparkle.size}
                    color={sparkle.color}
                    style={sparkle.style}
                  />
                ))}

              {/* Sticker Element */}
              {name && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    right: 16,
                    transform: "rotate(-3deg)",
                    zIndex: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.1rem",
                      color: "#666",
                      bgcolor: "rgba(255,255,255,0.95)",
                      p: "6px 12px",
                      borderRadius: "16px",
                      border: "1px dashed #e2cdb2",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    💌 From {name}
                  </Typography>
                </Box>
              )}

              {/* Card content area */}
              <Box
                sx={{
                  width: "100%",
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: 2,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    minHeight: 120,
                    overflow: "hidden",
                    bgcolor: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "16px",
                    boxShadow: "inset 0 2px 4px rgba(0,0,0,0.05)",
                  }}
                >
                  <img
                    src={cardData.design.image}
                    alt={cardData.design.title}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: "2rem", sm: "2.5rem" },
                    color: "#222",
                    mb: 0,
                    textShadow: "0 1px 2px rgba(0,0,0,0.05)",
                    mt: 2,
                  }}
                >
                  Happy Mother's Day!
                </Typography>
              </Box>
            </Box>

            {/* Back of card */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                bgcolor: "white",
                borderRadius: "24px",
                boxShadow: "0 12px 40px rgba(0, 0, 0, 0.12)",
                p: { xs: 3, sm: 4 },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                transform: "rotateY(180deg)",
                backfaceVisibility: "hidden",
                backgroundImage:
                  "linear-gradient(to bottom, rgba(255,255,255,0.95), rgba(255,255,255,0.98)), url('/assets/paper-texture.png')",
                backgroundSize: "cover",
                backgroundBlendMode: "overlay",
                overflow: "hidden",
                zIndex: 3,
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  alignItems: "center",
                  position: "relative",
                  height: "100%",
                }}
              >
                <AnimatePresence mode="wait">
                  {answeredQuestions
                    .slice(revealStep - 1, revealStep)
                    .map((item, index) => (
                      <motion.div
                        key={revealStep}
                        initial={{ opacity: 0, rotateY: 90 }}
                        animate={{ opacity: 1, rotateY: 0 }}
                        exit={{ opacity: 0, rotateY: -90 }}
                        transition={{
                          duration: 0.6,
                          type: "spring",
                          stiffness: 100,
                          damping: 15,
                        }}
                        style={{
                          perspective: "1000px",
                          transformStyle: "preserve-3d",
                          backfaceVisibility: "hidden",
                          width: "100%",
                          height: "100%",
                        }}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            p: { xs: 2, sm: 4 },
                            minHeight: 280,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: { xs: "1.8rem", sm: "2.2rem" },
                              color: "#4a3b8f",
                              fontFamily: "'Playfair Display', serif",
                              fontWeight: 600,
                              textAlign: "center",
                              lineHeight: 1.6,
                              maxWidth: "90%",
                              mx: "auto",
                              mb: 4,
                            }}
                          >
                            {transformToFirstPerson(item.question)}
                          </Typography>

                          {!isAnswerRevealed ? (
                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.5 }}
                              style={{ width: "100%", textAlign: "center" }}
                            >
                              <Typography
                                onClick={handleAnswerReveal}
                                sx={{
                                  fontSize: { xs: "1.4rem", sm: "1.6rem" },
                                  color: "#7c5cff",
                                  fontWeight: 500,
                                  cursor: "pointer",
                                  py: 4,
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  gap: 2,
                                  "&:hover": {
                                    color: "#4a3b8f",
                                  },
                                }}
                              >
                                <motion.span
                                  animate={{ y: [0, -4, 0] }}
                                  transition={{
                                    duration: 1.5,
                                    repeat: Infinity,
                                  }}
                                >
                                  👆
                                </motion.span>
                                Tap to reveal
                              </Typography>
                            </motion.div>
                          ) : (
                            <motion.div
                              initial={{ opacity: 0, scale: 0.95, y: 10 }}
                              animate={{ opacity: 1, scale: 1, y: 0 }}
                              transition={{
                                duration: 0.6,
                                type: "spring",
                                stiffness: 100,
                                damping: 15,
                              }}
                            >
                              <Box
                                sx={{
                                  bgcolor: "#f8f6ed",
                                  borderRadius: "24px",
                                  p: { xs: 3.5, sm: 6 },
                                  boxShadow: "0 8px 32px rgba(124,92,255,0.12)",
                                  width: "100%",
                                  maxWidth: 480,
                                  minHeight: 200,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  position: "relative",
                                  overflow: "hidden",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: (theme) =>
                                      `clamp(1.05rem, 2.5vw, 1.35rem)`,
                                    color: "#222",
                                    textAlign: "center",
                                    lineHeight: 1.8,
                                    wordBreak: "break-word",
                                    hyphens: "auto",
                                    overflowWrap: "break-word",
                                    ...(item.answer.length > 200 && {
                                      fontSize: (theme) =>
                                        `clamp(0.95rem, 2vw, 1.15rem)`,
                                    }),
                                    fontFamily: "'Source Sans Pro', sans-serif",
                                  }}
                                >
                                  {item.answer}
                                </Typography>
                              </Box>
                            </motion.div>
                          )}
                        </Box>
                      </motion.div>
                    ))}
                </AnimatePresence>
                {revealStep === answeredQuestions.length + 1 && (
                  <motion.div
                    key="signature"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{
                      duration: 0.6,
                      type: "spring",
                      stiffness: 100,
                      damping: 15,
                    }}
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: "2.5rem", sm: "3rem" },
                        color: "#4a3b8f",
                        fontFamily: "'Playfair Display', serif",
                        fontWeight: 600,
                        textAlign: "center",
                        lineHeight: 1.6,
                        mb: 3,
                      }}
                    >
                      Happy Mother's Day!
                    </Typography>
                    {name && (
                      <Typography
                        sx={{
                          fontSize: { xs: "1.4rem", sm: "1.6rem" },
                          color: "#666",
                          fontFamily: "'Playfair Display', serif",
                          fontWeight: 500,
                          textAlign: "center",
                          lineHeight: 1.6,
                          mb: 2.5,
                        }}
                      >
                        With love,
                      </Typography>
                    )}
                    {name && (
                      <Typography
                        sx={{
                          fontSize: { xs: "2rem", sm: "2.3rem" },
                          color: "#4a3b8f",
                          fontFamily: "'Dancing Script', cursive",
                          fontWeight: 600,
                          textAlign: "center",
                          lineHeight: 1.6,
                          transform: "rotate(-2deg)",
                        }}
                      >
                        — {name}
                      </Typography>
                    )}
                  </motion.div>
                )}
              </Box>
            </Box>
          </motion.div>
        </motion.div>
      </Box>

      {/* Navigation controls */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 600,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 2,
          mt: 4,
          mb: 2,
        }}
      >
        {!isFlipped ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{ width: "100%", display: "flex", justifyContent: "center" }}
          >
            <Typography
              sx={{
                fontSize: "1.2rem",
                color: "#7c5cff",
                fontWeight: 600,
                cursor: "pointer",
                px: 4,
                py: 2,
                borderRadius: "10px",
                transition: "all 0.2s",
                userSelect: "none",
                background: "#f3e6fa",
                width: 240,
                textAlign: "center",
                boxShadow: "0 2px 8px rgba(124,92,255,0.08)",
                "&:hover": {
                  background: "#e9d5ff",
                  transform: "translateY(-2px)",
                },
              }}
              onClick={handleCardTap}
            >
              Tap to open{" "}
              <motion.span
                animate={{ x: [0, 4, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                →
              </motion.span>
            </Typography>
          </motion.div>
        ) : (
          <>
            <Typography
              sx={{
                fontSize: "1.1rem",
                color: revealStep > 1 ? "#4a3b8f" : "#ccc",
                fontWeight: 600,
                cursor: revealStep > 1 ? "pointer" : "default",
                px: 4,
                py: 2.5,
                borderRadius: "10px",
                transition: "all 0.3s ease",
                userSelect: "none",
                background: revealStep > 1 ? "#f3e6fa" : "transparent",
                flex: 1,
                textAlign: "center",
                minHeight: "56px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow:
                  revealStep > 1 ? "0 4px 12px rgba(124,92,255,0.15)" : "none",
                "&:hover":
                  revealStep > 1
                    ? {
                        background: "#e9d5ff",
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 16px rgba(124,92,255,0.2)",
                      }
                    : {},
                mr: 1,
              }}
              onClick={
                revealStep > 1 ? () => setRevealStep(revealStep - 1) : undefined
              }
            >
              ← Previous
            </Typography>
            <Typography
              sx={{
                fontSize: "1.1rem",
                color:
                  revealStep <= answeredQuestions.length ? "#4a3b8f" : "#ccc",
                fontWeight: 600,
                cursor:
                  revealStep <= answeredQuestions.length
                    ? "pointer"
                    : "default",
                px: 4,
                py: 2.5,
                borderRadius: "10px",
                transition: "all 0.3s ease",
                userSelect: "none",
                background:
                  revealStep <= answeredQuestions.length
                    ? "#f3e6fa"
                    : "transparent",
                flex: 1,
                textAlign: "center",
                minHeight: "56px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow:
                  revealStep <= answeredQuestions.length
                    ? "0 4px 12px rgba(124,92,255,0.15)"
                    : "none",
                "&:hover":
                  revealStep <= answeredQuestions.length
                    ? {
                        background: "#e9d5ff",
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 16px rgba(124,92,255,0.2)",
                      }
                    : {},
                ml: 1,
              }}
              onClick={
                revealStep <= answeredQuestions.length ? handleNext : undefined
              }
            >
              {revealStep === answeredQuestions.length + 1
                ? "Thanks for reading 💕"
                : "Next →"}
            </Typography>
          </>
        )}
      </Box>

      {/* Make your own card button (only at end, if enabled) */}
      {showMakeYourOwnButton && revealStep === answeredQuestions.length + 1 && (
        <Box
          sx={{
            width: "100%",
            maxWidth: 600,
            display: "flex",
            justifyContent: "center",
            mt: 2,
          }}
        >
          <button
            style={{
              background: "#7c5cff",
              color: "#fff",
              fontWeight: 600,
              fontSize: "1.1rem",
              border: "none",
              borderRadius: "10px",
              padding: "14px 32px",
              boxShadow: "0 4px 16px rgba(124,92,255,0.13)",
              cursor: "pointer",
              transition: "background 0.2s, transform 0.2s",
            }}
            onClick={onMakeYourOwn}
          >
            Make your own card!
          </button>
        </Box>
      )}

      {/* Share buttons - only shown if showShareButtons is true */}
      {showShareButtons && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 2,
            mt: 1,
            position: "relative",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {/* Confirmation Toast anchored above share buttons */}
          <AnimatePresence>
            {showShareConfirmation && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 16 }}
                transition={{ type: "spring", stiffness: 200, damping: 20 }}
                style={{
                  position: "absolute",
                  bottom: "100%",
                  transform: "translateX(-50%)",
                  marginBottom: 10,
                  zIndex: 10,
                  width: "max-content",
                  maxWidth: "90vw",
                }}
              >
                <Box
                  sx={{
                    bgcolor: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(8px)",
                    borderRadius: "16px",
                    px: 3,
                    py: 1.5,
                    boxShadow: "0 8px 32px rgba(124,92,255,0.15)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    border: "1px solid rgba(124,92,255,0.1)",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#4a3b8f",
                      fontWeight: 500,
                      fontSize: { xs: "0.95rem", sm: "1.1rem" },
                      textAlign: "center",
                    }}
                  >
                    {shareMessage}
                  </Typography>
                </Box>
              </motion.div>
            )}
          </AnimatePresence>
          <IconButton
            onClick={() => {
              if (onCopyLink) {
                onCopyLink();
                setShareMessage("✨ Link copied – go make someone smile!");
                setShowShareConfirmation(true);
                setTimeout(() => {
                  setShowShareConfirmation(false);
                }, 2500);
              }
            }}
            sx={{
              bgcolor: "white",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              width: 50,
              height: 50,
              borderRadius: "10px",
              "&:hover": {
                bgcolor: "#f3e6fa",
                transform: "translateY(-2px)",
                boxShadow: "0 6px 16px rgba(124,92,255,0.2)",
              },
            }}
          >
            <ContentCopyIcon sx={{ fontSize: 30 }} />
          </IconButton>
          {navigator.share && (
            <IconButton
              onClick={async () => {
                if (onShare) {
                  try {
                    await onShare();
                    setShareMessage("✨ Link copied – go make someone smile!");
                    setShowShareConfirmation(true);
                    setTimeout(() => {
                      setShowShareConfirmation(false);
                    }, 2500);
                  } catch (error) {
                    console.error("Error sharing:", error);
                  }
                }
              }}
              sx={{
                bgcolor: "white",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                width: 50,
                height: 50,
                borderRadius: "10px",
                "&:hover": {
                  bgcolor: "#f3e6fa",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 16px rgba(124,92,255,0.2)",
                },
              }}
            >
              <ShareIcon sx={{ fontSize: 30 }} />
            </IconButton>
          )}
        </Box>
      )}

      {/* Footer */}
      <Typography
        sx={{
          position: "fixed",
          bottom: 8,
          left: 0,
          right: 0,
          textAlign: "center",
          fontSize: "0.9rem",
          color: "rgba(0,0,0,0.4)",
          opacity: 0.7,
        }}
      >
        Made with 💌 by Flying Comet Games
      </Typography>
    </Box>
  );
};

export default BaseCard;
